import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getAuthToken } from '../../utils/auth';
import { Send, Loader2, MessageCircle, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DealerSupportChat — a 1:1 support thread with RV Match AI.
 * Polls every 5s so the dealer sees admin replies without a page refresh.
 * Renders as a self-contained tab inside the DealerDashboard.
 */
export default function DealerSupportChat({ isDark = false }) {
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/support/thread`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(res.data?.messages || []);
      // best-effort mark-read on every poll so the sidebar badge clears
      // while the tab is open
      axios.post(`${API}/support/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API}/support/messages`, { body }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((prev) => [...prev, res.data]);
      setReply('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`} data-testid="dealer-support-chat">
      <div className={`px-5 py-4 border-b flex items-center gap-3 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
          <ShieldCheck size={20} className="text-white" />
        </div>
        <div>
          <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            RV Match AI Support
          </h3>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Chat directly with the RV Match AI team. Replies within one business day.
          </p>
        </div>
      </div>

      <div ref={scrollRef} className={`h-[420px] overflow-y-auto px-5 py-4 space-y-3 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
        {loading ? (
          <div className="text-center text-gray-400"><Loader2 className="animate-spin mx-auto" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle size={32} className="mx-auto text-gray-300 mb-2" />
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              No messages yet. Send us a note — we&apos;re here to help.
            </p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender === 'dealer';
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
                  mine
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : isDark
                      ? 'bg-gray-800 text-white rounded-bl-sm'
                      : 'bg-white border border-gray-200 text-gray-900 rounded-bl-sm'
                }`} data-testid={`support-msg-${m.id}`}>
                  {m.body}
                  <div className={`text-[10px] mt-1 ${mine ? 'text-blue-100' : isDark ? 'text-gray-400' : 'text-gray-400'}`}>
                    {new Date(m.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className={`px-5 py-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="flex items-end gap-2">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type a message — Enter to send"
            rows={2}
            className={`flex-1 rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'
            }`}
            data-testid="dealer-support-input"
          />
          <button
            onClick={send}
            disabled={sending || !reply.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5"
            data-testid="dealer-support-send"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
